// src/components/AccountRecovery.jsx
// Guided account-recovery flow shown when registration hard-rejects a
// duplicate (name+DOB+phone match) — see redimd-ops #203. Kept intentionally
// simple: plain inputs, no stepper, no multi-step confirms.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { api } from '../utilities/api';
import { isActivatedComplete, useUser } from '../contexts/UserContext';

const SUPPORT_PHONE_DISPLAY = '866-989-2873';
const SUPPORT_PHONE_TEL = 'tel:+18669892873';

function HelpLink() {
  return (
    <p className="mt-4 text-center text-sm text-neutral-dark">
      Need help?{' '}
      <a href={SUPPORT_PHONE_TEL} className="font-medium text-primary hover:underline">
        Call {SUPPORT_PHONE_DISPLAY}
      </a>
    </p>
  );
}

function AccountRecovery({ recoveryToken, rejectMessage }) {
  const navigate = useNavigate();
  const { checkSession } = useUser();

  // phase: reject | deadend | choose | code | password
  const [phase, setPhase] = useState('reject');
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const [channels, setChannels] = useState([]);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');

  const [code, setCode] = useState('');
  const [ticket, setTicket] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Sends the one-time code to a channel. Both channels are recognition-based
  // (masked-picker selection is the proof) -- the code always goes to the
  // on-file contact, so no `contact` is ever supplied here.
  // Backend: POST auth/remote_recovery_prove_contact
  //   body { recovery_token, channel } -> { status:'code_sent', channel, mask }
  //   or { error }.
  const sendCode = async (channel) => {
    setIsBusy(true);
    setError('');
    try {
      const payload = { recovery_token: recoveryToken, channel };
      const response = await api.recoveryProveContact(payload);
      if (response.status === 'code_sent') {
        setSelectedChannel(channel);
        setCode('');
        setPhase('code');
        return true;
      }
      setError(response.error || 'We could not send a code. Please try again.');
      return false;
    } catch {
      setError('Something went wrong sending your code. Please try again.');
      return false;
    } finally {
      setIsBusy(false);
    }
  };

  // Backend: POST auth/remote_recovery_options body { recovery_token } ->
  //   success: { status:'ok', first_name, channels:{ phone:{available,mask}, email:{available,mask} } }
  //   dead-end: { status:'dead_end', message }
  //   expired/invalid token: { error } (no status)
  const handleStartRecovery = async () => {
    setIsBusy(true);
    setError('');
    try {
      const response = await api.recoveryOptions({ recovery_token: recoveryToken });

      if (response.status === 'dead_end') {
        if (response.message) setError(response.message);
        setPhase('deadend');
        return;
      }

      if (response.status !== 'ok' || !response.channels) {
        setError(response.error || 'We could not look up your account. Please call us for help.');
        setPhase('deadend');
        return;
      }

      const channelsByName = response.channels;
      const availableChannels = Object.keys(channelsByName).filter((name) => channelsByName[name]?.available);
      setMaskedPhone(channelsByName.phone?.mask || '');
      setMaskedEmail(channelsByName.email?.mask || '');
      setChannels(availableChannels);

      if (availableChannels.length === 0) {
        setPhase('deadend');
        return;
      }

      if (availableChannels.length === 1) {
        await sendCode(availableChannels[0]);
        return;
      }

      setPhase('choose');
    } catch {
      setError('Something went wrong. Please call us for help.');
      setPhase('deadend');
    } finally {
      setIsBusy(false);
    }
  };

  const handleChooseChannel = async (channel) => {
    await sendCode(channel);
  };

  const handleResendCode = async () => {
    const sent = await sendCode(selectedChannel);
    if (sent) toast.success('A new code is on its way.');
  };

  // Backend: POST auth/remote_recovery_verify_code body { recovery_token, code }
  //   -> { status:'verified', ticket, channel } or { error }.
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setIsBusy(true);
    setError('');
    try {
      const response = await api.recoveryVerifyCode({ recovery_token: recoveryToken, code: code.trim() });
      if (response.status === 'verified' && response.ticket) {
        setTicket(response.ticket);
        setPhase('password');
      } else {
        setError(response.error || 'That code did not work. Please try again.');
      }
    } catch {
      setError('Something went wrong verifying your code. Please try again.');
    } finally {
      setIsBusy(false);
    }
  };

  // Backend: POST auth/remote_recovery_set_password body { ticket, password, email? }
  //   (reads `password`, NOT `new_password`; reads `email`, NOT `new_email`)
  //   -> { success:true, status:'recovered', jwt, redirect, adopted_email, ... } or { error }.
  // jwt is auto-persisted to localStorage generically by fetchWithJwt before this
  // resolves, so checkSession() below picks it up and hydrates UserContext.
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || newPassword.length < 8) {
      setError('Your password needs to be at least 8 letters or numbers.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = { ticket, password: newPassword };
      const trimmedEmail = newEmail.trim();
      if (trimmedEmail) payload.email = trimmedEmail;

      const response = await api.recoverySetPassword(payload);
      if (response.success) {
        await checkSession();
        toast.success("You're back in!", { duration: 6000 });
        if (isActivatedComplete(response.activated)) {
          navigate('/dashboard', { replace: true });
        } else {
          localStorage.setItem('redimdwm_registration_data', JSON.stringify({
            userid: response.userid,
            firstname: response.firstname,
            user_name: response.user_name,
            email: response.email,
            activated: '0',
            registration_step: response.registration_step || 'register',
          }));
          navigate('/register', { replace: true });
        }
      } else {
        setError(response.error || 'We could not save your password. Please try again.');
      }
    } catch {
      setError('Something went wrong saving your password. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col">
      {error && (
        <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm" role="alert">
          {error}
        </div>
      )}

      {phase === 'reject' && (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-primary mb-3">We found an existing account</h3>
          <p className="mb-6 text-neutral-darkest">{rejectMessage}</p>
          <button
            type="button"
            onClick={handleStartRecovery}
            disabled={isBusy}
            className={`w-full min-h-[44px] bg-button text-white py-3 rounded-md font-semibold hover:bg-button-light transition duration-200 ${isBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isBusy ? 'Looking up your account...' : 'Get back into my account'}
          </button>
        </div>
      )}

      {phase === 'deadend' && (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-primary mb-3">We can't verify this online</h3>
          <p className="mb-4 text-neutral-darkest">
            Please call us and we'll help you get back into your account.
          </p>
          <a
            href={SUPPORT_PHONE_TEL}
            className="inline-block w-full min-h-[44px] bg-button text-white py-3 px-4 rounded-md font-semibold hover:bg-button-light transition duration-200"
          >
            Call {SUPPORT_PHONE_DISPLAY}
          </a>
        </div>
      )}

      {phase === 'choose' && (
        <div>
          <h3 className="text-lg font-semibold text-primary mb-3 text-center">How should we reach you?</h3>
          <div className="flex flex-col gap-3">
            {channels.includes('phone') && (
              <button
                type="button"
                onClick={() => handleChooseChannel('phone')}
                disabled={isBusy}
                className="w-full min-h-[44px] border border-neutral rounded-md p-4 text-left hover:bg-neutral-light transition duration-150 disabled:opacity-50"
              >
                <span className="block font-semibold text-neutral-darkest">Text me</span>
                <span className="block text-sm text-neutral-dark">{maskedPhone}</span>
              </button>
            )}
            {channels.includes('email') && (
              <button
                type="button"
                onClick={() => handleChooseChannel('email')}
                disabled={isBusy}
                className="w-full min-h-[44px] border border-neutral rounded-md p-4 text-left hover:bg-neutral-light transition duration-150 disabled:opacity-50"
              >
                <span className="block font-semibold text-neutral-darkest">Email me</span>
                <span className="block text-sm text-neutral-dark">{maskedEmail}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {phase === 'code' && (
        <form onSubmit={handleCodeSubmit}>
          <h3 className="text-lg font-semibold text-primary mb-3 text-center">Enter your code</h3>
          <p className="mb-4 text-sm text-neutral-dark text-center">
            We sent a 6-digit code to {selectedChannel === 'phone' ? (maskedPhone || 'your phone') : (maskedEmail || 'your email')}.
          </p>
          <label htmlFor="recovery-code" className="sr-only">6-digit code</label>
          <input
            id="recovery-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Enter 6-digit code"
            required
            style={{ fontSize: '16px' }}
            className="w-full text-center tracking-[0.3em] text-xl font-semibold p-3 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary mb-4"
          />
          <button
            type="submit"
            disabled={isBusy || code.length !== 6}
            className="w-full min-h-[44px] bg-button text-white py-3 rounded-md font-semibold hover:bg-button-light transition duration-200 disabled:opacity-50"
          >
            {isBusy ? 'Verifying...' : 'Verify code'}
          </button>
          <button
            type="button"
            onClick={handleResendCode}
            disabled={isBusy}
            className="w-full mt-3 text-primary hover:underline text-sm disabled:opacity-50"
          >
            Send a new code
          </button>
        </form>
      )}

      {phase === 'password' && (
        <form onSubmit={handleSetPassword}>
          <h3 className="text-lg font-semibold text-primary mb-3 text-center">Set a new password</h3>
          <div className="relative mb-2">
            <label htmlFor="recovery-new-password" className="sr-only">New password</label>
            <input
              id="recovery-new-password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
              style={{ fontSize: '16px' }}
              className="w-full p-3 pr-10 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="New password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              )}
            </button>
          </div>
          <p className="mb-4 text-xs text-neutral-dark">At least 8 letters or numbers.</p>

          <label htmlFor="recovery-new-email" className="block text-sm font-medium text-neutral-dark mb-1">
            Update my email (optional)
          </label>
          <input
            id="recovery-new-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="your personal email"
            style={{ fontSize: '16px' }}
            className="w-full p-3 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary mb-4"
          />

          <button
            type="submit"
            disabled={isSaving}
            className="w-full min-h-[44px] bg-button text-white py-3 rounded-md font-semibold hover:bg-button-light transition duration-200 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save my password'}
          </button>
        </form>
      )}

      <HelpLink />
    </div>
  );
}

export default AccountRecovery;
